// Admin client for the CPA/commission engine (V26) — mirrors
// AdminDealController, AdminConversionController, AdminFinanceController and
// AdminPostbackTokenController. All money integer USD micros on the wire.
import { api } from "@/lib/api";
import type { PageResponse } from "@/types/api";
import type {
    CampaignCommercialsDto,
    ConversionDto,
    CsvCommitResponse,
    CsvImportRequest,
    CsvPreviewResponse,
    DealAccrualDto,
    DealDto,
    ManualConversionRequest,
    MarginRowDto,
    PostbackTokenDto,
    UpsertDealRequest,
} from "@/types/cpa";

const DEALS = "/admin/deals";
const CONVERSIONS = "/admin/conversions";
const FINANCE = "/admin/finance";

// ─── Deals ──────────────────────────────────────────────────────────────────

/** Exactly ONE of the three filters must be set — the backend 400s otherwise. */
export interface ListDealsFilter {
    advertiserId?: number;
    affiliateId?: number;
    campaignId?: number;
}

export async function listDeals(filter: ListDealsFilter): Promise<DealDto[]> {
    const { data } = await api.get<DealDto[]>(DEALS, { params: filter });
    return data;
}

export async function createDeal(req: UpsertDealRequest): Promise<DealDto> {
    const { data } = await api.post<DealDto>(DEALS, req);
    return data;
}

export async function updateDeal(id: number, req: UpsertDealRequest): Promise<DealDto> {
    const { data } = await api.patch<DealDto>(`${DEALS}/${id}`, req);
    return data;
}

/** 409 with {error} once ledger rows reference the deal — end it instead. */
export async function deleteDeal(id: number): Promise<void> {
    await api.delete(`${DEALS}/${id}`);
}

/** Operator confirms lead quality after the test phase — accrual resumes at full price. */
export async function confirmDealTest(id: number): Promise<DealDto> {
    const { data } = await api.post<DealDto>(`${DEALS}/${id}/confirm-test`);
    return data;
}

/** Re-run pricing over the deal's live ledger after terms changed. Returns the re-priced count. */
export async function repriceDeal(id: number): Promise<number> {
    const { data } = await api.post<{ repriced: number }>(`${DEALS}/${id}/reprice`);
    return data.repriced;
}

// ─── Finance ────────────────────────────────────────────────────────────────

export interface FinanceFilter {
    advertiserId?: number;
    affiliateId?: number;
    campaignId?: number;
}

export async function getMarginRows(month: string, filter: FinanceFilter = {}): Promise<MarginRowDto[]> {
    const { data } = await api.get<MarginRowDto[]>(`${FINANCE}/margin`, {
        params: { month, ...filter },
    });
    return data;
}

export async function listAccruals(
    filter: FinanceFilter & { dealId?: number },
    page = 0,
    size = 50,
): Promise<PageResponse<DealAccrualDto>> {
    const { data } = await api.get<PageResponse<DealAccrualDto>>(`${FINANCE}/accruals`, {
        params: { ...filter, page, size },
    });
    return data;
}

/** 404 when the campaign doesn't exist. */
export async function getCampaignCommercials(campaignId: number): Promise<CampaignCommercialsDto> {
    const { data } = await api.get<CampaignCommercialsDto>(`${FINANCE}/campaign-commercials`, {
        params: { campaignId },
    });
    return data;
}

// ─── Conversions ────────────────────────────────────────────────────────────

export async function listConversions(
    matchStatus: string | undefined,
    page = 0,
    size = 50,
): Promise<PageResponse<ConversionDto>> {
    const { data } = await api.get<PageResponse<ConversionDto>>(CONVERSIONS, {
        params: { matchStatus: matchStatus || undefined, page, size },
    });
    return data;
}

export async function getUnmatchedConversionCount(): Promise<number> {
    const { data } = await api.get<{ unmatched: number }>(`${CONVERSIONS}/unmatched-count`);
    return typeof data?.unmatched === "number" ? data.unmatched : 0;
}

/** Manual "Record FTD" from the lead drawer. 409 when one is already recorded. */
export async function recordManualConversion(req: ManualConversionRequest): Promise<ConversionDto> {
    const { data } = await api.post<ConversionDto>(`${CONVERSIONS}/manual`, req);
    return data;
}

/** Resolve an unmatched conversion to a lead; the backend prices it immediately. */
export async function resolveConversion(id: number, leadId: number): Promise<ConversionDto> {
    const { data } = await api.post<ConversionDto>(`${CONVERSIONS}/${id}/resolve`, { leadId });
    return data;
}

export async function csvPreview(req: CsvImportRequest): Promise<CsvPreviewResponse> {
    const { data } = await api.post<CsvPreviewResponse>(`${CONVERSIONS}/csv/preview`, req);
    return data;
}

export async function csvCommit(req: CsvImportRequest): Promise<CsvCommitResponse> {
    const { data } = await api.post<CsvCommitResponse>(`${CONVERSIONS}/csv/commit`, req);
    return data;
}

// ─── Postback tokens ────────────────────────────────────────────────────────

export async function listPostbackTokens(advertiserId: number): Promise<PostbackTokenDto[]> {
    const { data } = await api.get<PostbackTokenDto[]>(`/admin/advertisers/${advertiserId}/postback-tokens`);
    return data;
}

export async function createPostbackToken(advertiserId: number): Promise<PostbackTokenDto> {
    const { data } = await api.post<PostbackTokenDto>(`/admin/advertisers/${advertiserId}/postback-tokens`);
    return data;
}

export async function revokePostbackToken(advertiserId: number, tokenId: number): Promise<void> {
    await api.post(`/admin/advertisers/${advertiserId}/postback-tokens/${tokenId}/revoke`);
}
